import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Button,
  Chip,
  CircularProgress,
  IconButton,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import LogoutIcon from '@mui/icons-material/Logout'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

import { useAppDispatch } from '../../redux/store'
import { fetchMyOrganizations } from '../../redux/controlPlane/controlPlaneActions'
import { controlPlaneLogout } from '../../redux/controlPlane/controlPlaneSlice'
import {
  selectControlPlaneToken,
  selectControlPlaneOwner,
  selectMyOrganizations,
  selectOrganizationsLoading,
} from '../../redux/controlPlane/controlPlaneSelectors'

const AccountOrganizations: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const token = useSelector(selectControlPlaneToken)
  const owner = useSelector(selectControlPlaneOwner)
  const organizations = useSelector(selectMyOrganizations)
  const loading = useSelector(selectOrganizationsLoading)

  useEffect(() => {
    if (!token) {
      navigate('/account/login')
      return
    }
    dispatch(fetchMyOrganizations())
  }, [token, navigate, dispatch])

  const handleLogout = () => {
    dispatch(controlPlaneLogout())
    navigate('/account/login')
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fbfbfb' }}>
      <Box sx={{ bgcolor: '#132743', color: 'white', py: 2, px: 3 }}>
        <Container maxWidth="md">
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <img src="/incyia.jpg" alt="Logo" style={{ height: 32, borderRadius: 4 }} />
              <Typography fontWeight={700}>{t('account.portalTitle', 'Account Portal')}</Typography>
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography variant="body2">{owner?.email}</Typography>
              <IconButton onClick={handleLogout} sx={{ color: 'white' }} size="small">
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight={700}>
            {t('account.myOrganizations', 'My organizations')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ bgcolor: '#244A9A' }}
            onClick={() => navigate('/get-started')}
          >
            {t('account.newOrganization', 'New organization')}
          </Button>
        </Stack>

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : organizations.length === 0 ? (
          <Paper sx={{ p: 4, borderRadius: '12px', textAlign: 'center' }}>
            <Typography color="text.secondary" mb={2}>
              {t('account.noOrganizations', "You don't manage any organization yet.")}
            </Typography>
            <Button variant="contained" sx={{ bgcolor: '#244A9A' }} onClick={() => navigate('/get-started')}>
              {t('account.createOne', 'Create your first organization')}
            </Button>
          </Paper>
        ) : (
          <Stack spacing={2}>
            {organizations.map((org) => (
              <Paper
                key={org.organizationId}
                sx={{
                  p: 2.5,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  '&:hover': { boxShadow: '0 2px 10px rgba(0,0,0,0.08)' },
                }}
                onClick={() => navigate(`/account/organizations/${org.organizationId}/billing`)}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      bgcolor: '#eff6ff',
                      color: '#1d4ed8',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <BusinessOutlinedIcon />
                  </Box>
                  <Box>
                    <Typography fontWeight={600}>{org.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {org.slug}
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Chip label={org.status} size="small" color={org.status === 'ACTIVE' ? 'success' : 'default'} />
                  <ArrowForwardIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Container>
    </Box>
  )
}

export default AccountOrganizations
